import { graphql } from 'app/generated/gql'
import type { Client } from '@urql/core'

import { action, makeObservable, observable, runInAction, computed } from 'mobx'
import { logWarn } from 'lib/logging'
import { categoryFromWire, type ToggleMetadata } from 'app/components/feature-toggles/featureToggleMetadata'
import { EntityStore, type ListResult } from './EntityStore'

// Read-only view over the API's `featureToggleDefinitions` query — a per-name
// deduplicated list of FeatureToggle rows carrying the shared metadata
// (displayName/category). Create/edit go through the main FeatureToggleStore
// (createFeatureToggle/updateFeatureToggle), which sync metadata across
// same-name rows server-side.
//
// TODO(codegen): once dev deploys PR #246 and `bun run codegen` regenerates the
// schema, replace this local shape with the generated FeatureToggle type.
export type FeatureToggleDefinitionJson = {
  id: string
  name: string
  displayName?: string | null
  description?: string | null
  // Wire value of FeatureToggleCategory (UPPERCASE, e.g. `CONTACTS`).
  category?: string | null
  archived?: boolean
  release?: string | null
  createdDate?: string
  updatedDate?: string
}

type DefListResult = ListResult<FeatureToggleDefinitionJson>

const LIST_QUERY = graphql(`
  query FeatureToggleDefinitions($search: String, $pagination: PaginationInput, $sort: SortInput) {
    featureToggleDefinitions(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        name
        displayName
        description
        category
        archived
        release
        createdDate
        updatedDate
      }
      total
      page
    }
  }
`)

export class FeatureToggleDefinitionStore extends EntityStore<FeatureToggleDefinitionJson, DefListResult> {
  byName = observable.map<string, FeatureToggleDefinitionJson>()
  loaded = false

  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      extractList: (data) => (data as { featureToggleDefinitions: DefListResult }).featureToggleDefinitions,
    })
    // makeObservable (not makeAutoObservable): extends EntityStore (subclass) and
    // declares `metaByName` as an explicit computed.
    makeObservable(this, {
      byName: observable,
      loaded: observable,
      metaByName: computed,
      archivedNames: computed,
      releaseByName: computed,
      loadAll: action.bound,
    })
  }

  // ToggleMetadata view of the definitions, keyed by slug. Stable MobX computed
  // so hooks can use it as a memo dependency. Category is mapped from its
  // UPPERCASE wire value back to the title-case the UI uses.
  get metaByName(): Map<string, ToggleMetadata> {
    const m = new Map<string, ToggleMetadata>()
    this.byName.forEach((d, name) => {
      const category = categoryFromWire(d.category)
      if (!d.displayName && !category) return
      m.set(name, {
        label: d.displayName ?? name,
        description: d.description ?? ``,
        category: category ?? `Internal`,
      })
    })
    return m
  }

  get archivedNames(): ReadonlySet<string> {
    return new Set([...this.byName.values()].filter((d) => d.archived).map((d) => d.name))
  }

  get releaseByName(): ReadonlyMap<string, string> {
    return new Map([...this.byName.values()].flatMap((d) => (d.release ? [[d.name, d.release] as const] : [])))
  }

  // Load every definition in one request. Fails SILENTLY (logWarn, no toast) so
  // the settings page still works before the backend is deployed — the UI falls
  // back to the static featureToggleMetadata map.
  async loadAll() {
    const result = await this.client.query(LIST_QUERY, { pagination: { limit: 1000 } }).toPromise()
    runInAction(() => {
      this.loaded = true
      if (result.error) {
        logWarn(`feature toggle definitions unavailable`, result.error.message)
        return
      }
      const hits =
        (result.data as { featureToggleDefinitions?: DefListResult } | undefined)?.featureToggleDefinitions?.hits ?? []
      this.byName.clear()
      for (const d of hits) this.byName.set(d.name, d)
      this.list = { hits, total: hits.length, page: 1 }
    })
  }

  reset() {
    runInAction(() => {
      super.reset()
      this.byName.clear()
      this.loaded = false
    })
  }
}
